import React from 'react'
import { Card, CardBody, Form } from 'reactstrap'
import { Spin, message, Button } from 'antd'
import { PDFDocument } from 'pdf-lib'
import { getFbaBoxLabelInfo, markFbaBoxLabelled } from '../../services/api/apiBatchShip'
import { convertDownloadLink } from '../../services/api/EntranceServices'
import { getAccessToken } from '../../services/AuthServices'
import isPdfUrl from '../helpers/isPdfUrl'
import printJS from 'print-js'

// FAC-23949: Màn scan & in label thùng FBA.
// FAC-24140: Gộp Amazon box label (PDF) + ship label (PNG) thành 1 file PDF & in 1 LẦN duy nhất
//            (bỏ luồng "mở lần lượt từng file" cũ).
// Flow:
// 1. User quét mã thùng -> gọi API label-info lấy thông tin thùng, validate phải có đủ 2 label
//    (Amazon box label + ship label).
// 2. Tải bytes 2 file (data URL decode trực tiếp / URL S3 qua proxy get-photo-s3 tránh CORS),
//    gộp bằng pdf-lib thành 1 PDF 2 trang CÙNG KHỔ (2 label cùng kích thước): Amazon PDF làm trang gốc,
//    ship label thêm 1 trang mới cùng khổ trang Amazon + scale ảnh vừa khít (giữ tỉ lệ, canh giữa).
// 3. Mở hộp thoại in file gộp 1 lần. KHÔNG tự đánh dấu labelled sau khi đóng hộp thoại in
//    (print-js không phân biệt được "in xong" vs "Hủy" -> dễ đánh dấu nhầm).
// 4. Nhân viên in & dán xong 2 label -> bấm nút "Xác nhận đã in xong" -> mới call API labelled
//    (BE lưu user + thời gian) -> reset & focus để quét thùng tiếp theo.
// 5. Nếu gộp lỗi (fetch/CORS/định dạng file) -> BÁO LỖI, KHÔNG in lần lượt & KHÔNG đánh dấu
//    (tránh đánh dấu nhầm thùng chưa in đủ) -> nhân viên quét lại.

// Response API label-info (màn Dán Label FBA): amazon_box_label + ship_label.
// (Khác với màn Lô ship / List thùng dùng label_ship_details.)
const getFbaLabelUrl = (box) => (box && box.amazon_box_label) || ''
const getShipLabelUrl = (box) => {
    const s = box && box.ship_label
    return (typeof s === 'string' ? s : (s && s.url)) || ''
}

// Fetch bytes từ 1 URL cụ thể (kèm auth tùy chọn). Ném lỗi (gắn .status) nếu status không ok.
const fetchBytes = async (fetchUrl, withAuth) => {
    const resp = await fetch(fetchUrl, withAuth
        ? { headers: { Authorization: `Bearer ${getAccessToken()}` } }
        : {})
    if (!resp.ok) {
        const err = new Error(`Tải file label thất bại (${resp.status})`)
        err.status = resp.status
        throw err
    }
    return new Uint8Array(await resp.arrayBuffer())
}

// Tải bytes 1 file label về browser để gộp.
// - data:...;base64 (ship label vẽ từ canvas): decode trực tiếp, khỏi fetch.
// - URL file:
//   1) Ưu tiên proxy same-origin get-photo-s3 (tránh CORS) + auth Bearer (proxy yêu cầu token,
//      thiếu -> 401 "Token is invalid").
//   2) Proxy chỉ cho phép 1 số host trong whitelist BE; host lạ (vd Amazon box label) -> 403
//      "Host not allowed". CHỈ khi gặp 403 mới fallback fetch TRỰC TIẾP URL gốc (yêu cầu host bật
//      CORS - fetch cần CORS dù iframe/img preview thì không). 401/5xx -> ném lại lỗi gốc để báo
//      đúng nguyên nhân (vd hết phiên), không che bằng lỗi CORS của nhánh fallback.
const fetchLabelBytes = async (url) => {
    if (url.startsWith('data:')) {
        const b64 = url.split(',')[1] || ''
        return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0))
    }
    try {
        return await fetchBytes(convertDownloadLink(url, true), true)
    } catch (e) {
        if (e && e.status === 403) return await fetchBytes(url, false)
        throw e
    }
}

// Nhúng ảnh vào PDF theo đúng định dạng (JPEG magic FF D8, còn lại coi như PNG).
const embedImage = (doc, bytes) =>
    bytes[0] === 0xff && bytes[1] === 0xd8 ? doc.embedJpg(bytes) : doc.embedPng(bytes)

// Gộp Amazon box label (PDF) + ship label (ảnh) thành 1 file PDF, trả về blob URL để in.
// 2 label CÙNG KÍCH THƯỚC -> trang ship label dùng CÙNG khổ trang với Amazon PDF, ảnh được scale
// vừa khít trang (giữ tỉ lệ, canh giữa) -> 2 trang in ra CÙNG cỡ giấy, không méo barcode/chữ.
// (Không dùng kích thước pixel PNG làm khổ trang vì pixel != point -> khổ trang sẽ sai đơn vị.)
const buildMergedPdfUrl = async (fbaUrl, shipUrl) => {
    const [pdfBytes, imgBytes] = await Promise.all([
        fetchLabelBytes(fbaUrl),
        fetchLabelBytes(shipUrl),
    ])
    const doc = await PDFDocument.load(pdfBytes)
    const img = await embedImage(doc, imgBytes)
    // Trang ship label lấy cùng khổ trang đầu của Amazon PDF -> 2 trang cùng cỡ giấy.
    const { width: pageW, height: pageH } = doc.getPage(0).getSize()
    const page = doc.addPage([pageW, pageH])
    // Scale ảnh vừa khít trang (contain), giữ tỉ lệ, canh giữa.
    const scale = Math.min(pageW / img.width, pageH / img.height)
    const drawW = img.width * scale
    const drawH = img.height * scale
    page.drawImage(img, { x: (pageW - drawW) / 2, y: (pageH - drawH) / 2, width: drawW, height: drawH })
    const bytes = await doc.save()
    return URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }))
}

class BoxLabelPage extends React.Component {
    inputRef = React.createRef()

    state = {
        barcode: '',
        box: null,
        boxId: null,
        batchInfo: null,
        isLoading: false,
        error: '',
        shipPrinted: false,
        markLoading: false,
    }

    _handleChange = (e) => {
        this.setState({ barcode: e.target.value })
    }

    // In lại 1 label riêng lẻ (nút "In lại" từng label): ảnh (PNG) in qua iframe ẩn; PDF mở tab mới.
    // Chỉ dùng cho thao tác in lại thủ công từng label - KHÔNG dùng cho luồng quét tự động.
    _openPrint = (url) => {
        if (!url) return

        if (isPdfUrl(url)) {
            printJS({ printable: url, type: 'pdf' })
            return
        }

        const iframe = document.createElement('iframe')
        iframe.style.cssText = 'position:fixed;left:-9999px;width:0;height:0;border:0'
        document.body.appendChild(iframe)

        const cleanup = () => {
            setTimeout(() => { iframe.parentNode && iframe.parentNode.removeChild(iframe) }, 1000)
        }
        const doPrint = () => {
            try {
                iframe.contentWindow.focus()
                iframe.contentWindow.print()
            } catch (e) { /* noop */ }
            cleanup()
        }

        const doc = iframe.contentWindow.document
        doc.open()
        doc.write('<!doctype html><html><head><title>In label</title></head><body style="margin:0;text-align:center"></body></html>')
        doc.close()

        const img = doc.createElement('img')
        img.style.maxWidth = '100%'
        img.onload = doPrint
        img.onerror = cleanup
        img.src = url
        doc.body.appendChild(img)
    }

    _handleSubmit = async (e) => {
        e.preventDefault()
        const { barcode } = this.state
        const vBarcode = barcode.trim()
        if (!vBarcode) return

        this.setState({ barcode: '', isLoading: true, error: '', box: null, boxId: null, batchInfo: null, shipPrinted: false })

        try {
            const boxId = vBarcode.replace('#', '').split('_')[0]
            if (!boxId) throw new Error('Mã thùng không hợp lệ')

            const { data: res } = await getFbaBoxLabelInfo(boxId)
            const { success, data, message: mess } = res
            if (!success) throw new Error(mess || 'Không lấy được thông tin thùng')

            const box = data || {}
            const batchInfo = box.batch_ship || box.batch || null

            // Validate phải có đủ 2 label trước khi cho in.
            const fbaUrl = getFbaLabelUrl(box)
            const shipUrl = getShipLabelUrl(box)
            const missing = []
            if (!fbaUrl) missing.push('Amazon box label')
            if (!shipUrl) missing.push('Ship label')
            if (missing.length > 0) {
                this.setState({ box, boxId, batchInfo, error: `Thùng #${boxId} thiếu: ${missing.join(' & ')}` })
                return
            }

            this.setState({ box, boxId, batchInfo }, () => {
                // Gộp 2 label thành 1 file & in 1 lần. KHÔNG tự đánh dấu - chờ nhân viên bấm
                // "Xác nhận đã in xong" sau khi đã in & dán xong (tránh đánh dấu nhầm khi Hủy in).
                this._printCombinedSafe(box)
            })
        } catch (err) {
            this.setState({ error: `[Mã thùng: ${vBarcode}] ${err.message}` })
        } finally {
            this.setState({ isLoading: false })
        }
    }

    _handleReset = () => {
        this.setState({ box: null, boxId: null, batchInfo: null, error: '', barcode: '', shipPrinted: false })
    }

    // Gộp 2 label thành 1 PDF & mở hộp thoại in 1 lần. KHÔNG tự đánh dấu labelled ở đây
    // (print-js không biết nhân viên bấm In hay Hủy). Ném lỗi nếu tải/gộp file thất bại.
    _printCombined = async (box) => {
        const mergedUrl = await buildMergedPdfUrl(getFbaLabelUrl(box), getShipLabelUrl(box))
        printJS({
            printable: mergedUrl,
            type: 'pdf',
            // Giải phóng blob URL sau khi đóng hộp thoại in.
            onPrintDialogClose: () => setTimeout(() => URL.revokeObjectURL(mergedUrl), 1000),
        })
    }

    // Báo BE thùng đã dán đủ label (BE lưu user + thời gian) rồi reset & focus để quét tiếp.
    _markLabelled = async (boxId) => {
        if (!boxId) {
            message.warning('Đã mở file gộp để in, nhưng thiếu mã thùng nên chưa cập nhật được trạng thái.')
            return
        }
        this.setState({ markLoading: true })
        try {
            const { data } = await markFbaBoxLabelled(boxId)
            const { success, message: mess } = data
            if (success) {
                message.success('Đã in đủ 2 label & cập nhật trạng thái thùng!')
                // Clear thông tin & focus lại input để scan thùng tiếp theo.
                this._handleReset()
                if (this.inputRef.current) this.inputRef.current.focus()
            } else {
                message.error(`Thùng #${boxId}: ${mess || 'Cập nhật trạng thái dán label không thành công!'}`)
            }
        } catch (error) {
            message.error(`Thùng #${boxId}: ${error.message}`)
        } finally {
            this.setState({ markLoading: false })
        }
    }

    // Gộp & in 1 lần - dùng cho cả lúc quét thùng lẫn nút "In lại (gộp)". KHÔNG đánh dấu labelled;
    // việc đánh dấu do nhân viên chủ động bấm "Xác nhận đã in xong" (tránh đánh dấu nhầm khi Hủy in).
    _printCombinedSafe = async (box) => {
        if (!box) return

        this.setState({ markLoading: true })
        try {
            await this._printCombined(box)
        } catch (err) {
            // Gộp lỗi -> KHÔNG in lần lượt & KHÔNG đánh dấu (tránh đánh dấu nhầm thùng chưa in được).
            message.error(`Không gộp được 2 label (${err.message}). Vui lòng quét lại thùng.`)
        } finally {
            this.setState({ markLoading: false })
        }
    }

    // Nút "Xác nhận đã in xong": nhân viên xác nhận đã in & dán đủ 2 label -> mới đánh dấu labelled.
    _handleConfirmPrinted = () => {
        this._markLabelled(this.state.boxId)
    }

    _renderLabelPreview = (url) => {
        if (!url) {
            return <div className='BoxLabelMissing blink'>Chưa có label</div>
        }

        return isPdfUrl(url)
            ? (
                <iframe
                    className='BoxLabelPreviewPdf'
                    src={`${url}#toolbar=0&navpanes=0&scrollbar=0&view=Fit`}
                    title='Label preview'
                />
            )
            : <img className='BoxLabelPreviewImage' src={url} alt='Label preview' />
    }

    _renderContent = () => {
        const { box, boxId, batchInfo, error, shipPrinted, markLoading } = this.state

        if (error) {
            return (
                <div className='ShipContent mt-3'>
                    <div className='BoxLabelMissing blink text-center'>{error}</div>
                </div>
            )
        }

        if (!box) return null

        const fbaUrl = getFbaLabelUrl(box)
        const shipUrl = getShipLabelUrl(box)

        return (
            <div className='ShipContent mt-3'>
                <Card>
                    <div className='card-header d-flex align-items-center justify-content-between'>
                        <div>
                            <strong>Thùng:</strong> #{boxId}
                            {batchInfo && batchInfo.id && (
                                <span>
                                    &nbsp;&nbsp;
                                    <strong>Lô ship:</strong> {batchInfo.id}
                                </span>
                            )}
                            {batchInfo && (batchInfo.box_carrier || batchInfo.carrier) && (
                                <span>
                                    &nbsp;&nbsp;
                                    <strong>Carrier:</strong> {batchInfo.box_carrier || batchInfo.carrier}
                                </span>
                            )}
                            {shipPrinted && (
                                <span className='badge badge-success ml-2'>Đã in đủ 2 label</span>
                            )}
                        </div>
                        <div className='d-flex align-items-center'>
                            <Button
                                type='primary'
                                loading={markLoading}
                                className='mr-2'
                                onClick={this._handleConfirmPrinted}
                            >
                                <i className='fa fa-check mr-1' />
                                Xác nhận đã in xong
                            </Button>
                            <Button
                                loading={markLoading}
                                className='mr-2'
                                onClick={() => this._printCombinedSafe(box)}
                            >
                                <i className='fa fa-print mr-1' />
                                In lại (gộp)
                            </Button>
                            <button className='btn btn-secondary' onClick={this._handleReset}>
                                Quét tiếp
                            </button>
                        </div>
                    </div>
                    <CardBody>
                        <div className='BoxLabelGroup'>
                            <div className='BoxLabelItem'>
                                <div className='BoxLabelItemHeader d-flex align-items-center justify-content-between'>
                                    <strong>1. Amazon box label</strong>
                                    <Button onClick={() => this._openPrint(fbaUrl)}>
                                        <i className='fa fa-print mr-1' />
                                        In lại
                                    </Button>
                                </div>
                                <div className='BoxLabelPreview'>
                                    {this._renderLabelPreview(fbaUrl)}
                                </div>
                            </div>
                            <div className='BoxLabelItem'>
                                <div className='BoxLabelItemHeader d-flex align-items-center justify-content-between'>
                                    <strong>2. Ship label</strong>
                                    <Button onClick={() => this._openPrint(shipUrl)}>
                                        <i className='fa fa-print mr-1' />
                                        In lại
                                    </Button>
                                </div>
                                <div className='BoxLabelPreview'>
                                    {this._renderLabelPreview(shipUrl)}
                                </div>
                            </div>
                        </div>
                    </CardBody>
                </Card>
            </div>
        )
    }

    render() {
        const { barcode, isLoading, markLoading } = this.state

        return (
            <Spin spinning={isLoading || markLoading} tip='Đang tải...'>
                <div className='ShipPage'>
                    <div className='PrintSearch'>
                        <Card>
                            <div className='card-header bg-success text-white header-elements-inline justify-content-start flex-wrap'>
                                <h1 className='card-title mr-5 mb-0'>
                                    Dán Label FBA - Quét hoặc nhập mã QR của thùng FBA
                                </h1>
                                <small className='w-100 mt-1'>
                                    Quét mã thùng để tự gộp Amazon box label + ship label thành 1 file và in 1 lần; sau khi in & dán xong 2 label, bấm "Xác nhận đã in xong" để cập nhật trạng thái thùng.
                                </small>
                            </div>
                            <CardBody>
                                <div className='ShipSearchBody'>
                                    <Form className='d-flex justify-content-center' onSubmit={this._handleSubmit}>
                                        <input
                                            className='InputSearch form-control'
                                            name='barcode'
                                            value={barcode}
                                            onChange={this._handleChange}
                                            ref={this.inputRef}
                                            autoFocus
                                        />
                                    </Form>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                    {this._renderContent()}
                </div>
            </Spin>
        )
    }
}

export default BoxLabelPage
